import {inject, LOCALE_ID, Pipe, PipeTransform} from '@angular/core';

/**
 * An ISO 8601 date and time, with a "T" or a space, and an optional zone: "Z" or an offset. Laravel serializes dates
 * as "2026-10-27T08:05:00.000000Z", and its database format is "2026-10-27 08:05:00".
 */
const ISO_DATE_TIME = /^(\d{4}-\d{2}-\d{2})[ T](\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?)(Z|[+-]\d{2}:\d{2})?$/;

const formats = new Map<string, Intl.DateTimeFormat>();

/**
 * Whether the day exists: Date rolls an impossible day over ("2026-02-30" becomes March 2) instead of refusing it
 */
function isCalendarDate(date: string): boolean {
	const [year, month, day] = date.split('-').map(Number);
	const calendar = new Date(Date.UTC(year, month - 1, day));

	return calendar.getUTCFullYear() === year && calendar.getUTCMonth() === month - 1 && calendar.getUTCDate() === day;
}

/**
 * Formats an API date for display in the viewer's time zone, with the zone's short name, so a time is never read
 * in the wrong zone: "Oct 27, 2026, 10:00 AM GMT+2". An empty or invalid date gives an empty string.
 *
 * It reads ISO 8601 only. A date without a zone is UTC, as the API stores it: a browser would read it as local time.
 * Other text, such as "October 27, 2026 8:05 AM", gives an empty string rather than a time in a guessed zone.
 *
 * Usage: `{{ copy.synced_at | dateTime }}`. The second argument sets the time zone instead of the browser's; an
 * unknown zone gives an empty string.
 */
@Pipe({
	name: 'dateTime',
	standalone: true,
})
export class DateTimePipe implements PipeTransform {
	// The app sets no locale (English only): if it ever follows the translation language, dates follow too
	private readonly locale = inject(LOCALE_ID);

	transform(value: string | null | undefined, timeZone?: string): string {
		const parts = value ? ISO_DATE_TIME.exec(value) : null;
		if (!parts) {
			return '';
		}

		const [, date, time, zone] = parts;
		const moment = new Date(`${date}T${time}${zone ?? 'Z'}`);
		const format = this.format(timeZone);
		if (!isCalendarDate(date) || Number.isNaN(moment.getTime()) || !format) {
			return '';
		}

		return format.format(moment);
	}

	/**
	 * Null for an unknown time zone, which would otherwise break the template that shows it
	 */
	private format(timeZone: string | undefined): Intl.DateTimeFormat | null {
		const key = `${this.locale}|${timeZone ?? ''}`;
		let format = formats.get(key);
		if (!format) {
			try {
				// Undefined time zone: the browser's
				format = new Intl.DateTimeFormat(this.locale, {
					year: 'numeric',
					month: 'short',
					day: 'numeric',
					hour: 'numeric',
					minute: '2-digit',
					timeZoneName: 'short',
					timeZone,
				});
			} catch {
				return null;
			}
			formats.set(key, format);
		}

		return format;
	}
}
